import { LitElement, css, html } from "lit";
import { customElement } from "lit/decorators.js";

import type { SignalState } from "../api/client";
import "../components/ld-signal";
import { icons } from "../icons";
import { STATES } from "../signal-state";
import { shared } from "../styles/shared";

const SECTIONS = [
  ["presentation", "Présentation"],
  ["sources", "Les sources"],
  ["voyants", "Les voyants"],
  ["interpretation", "Interpréter un rapport"],
  ["historique", "Historique et relance"],
  ["acces", "Accès et conservation"],
] as const;

@customElement("page-docs")
export class PageDocs extends LitElement {
  static styles = [
    shared,
    css`
      :host { display: block; }
      .layout { display: grid; grid-template-columns: 220px 1fr; gap: 48px; padding: 40px 0 100px; }
      @media (max-width: 860px) { .layout { grid-template-columns: 1fr; } .toc { position: static !important; } }
      .toc { position: sticky; top: calc(var(--header-h) + 24px); align-self: start; display: flex; flex-direction: column; gap: 2px; }
      .toc-label { font-size: 11px; font-weight: 700; color: var(--slate-400); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 10px; }
      .toc button { text-align: left; padding: 8px 12px; border-radius: var(--r-sm); font-size: 13.5px; color: var(--slate-600); background: none; border: 0; border-left: 2px solid transparent; }
      .toc button:hover { color: var(--ink); background: var(--slate-100); }
      section { margin-bottom: 56px; scroll-margin-top: calc(var(--header-h) + 20px); }
      h2 { font-size: 22px; font-weight: 800; letter-spacing: -.01em; margin-bottom: 14px; color: var(--navy-950); }
      p { color: var(--slate-600); font-size: 14.5px; line-height: 1.75; margin-bottom: 14px; max-width: 680px; }
      ul { margin: 14px 0; display: flex; flex-direction: column; gap: 10px; }
      li { font-size: 14px; padding-left: 22px; position: relative; line-height: 1.6; max-width: 660px; }
      li::before { content: ""; position: absolute; left: 0; top: 8px; width: 6px; height: 6px; border-radius: 50%; background: var(--blue-500); }
      .callout { border: 1px solid var(--blue-100); background: var(--blue-50); border-radius: var(--r-md); padding: 18px 20px; display: flex; gap: 14px; margin: 20px 0; max-width: 680px; }
      .callout svg { width: 18px; height: 18px; color: var(--blue-600); flex-shrink: 0; margin-top: 1px; }
      .callout p { margin: 0; font-size: 13.5px; }
      table { width: 100%; border-collapse: collapse; margin: 18px 0 8px; border: 1px solid var(--slate-200); max-width: 680px; }
      th, td { text-align: left; padding: 12px 16px; font-size: 13px; border-bottom: 1px solid var(--slate-200); vertical-align: top; }
      th { background: var(--slate-100); font-weight: 700; color: var(--slate-600); font-size: 11.5px; text-transform: uppercase; letter-spacing: .04em; }
      td:first-child { font-family: var(--font-mono); font-weight: 600; }
      .state-cell { display: flex; align-items: center; gap: 10px; font-family: var(--font-sans); }
    `,
  ];

  private goTo(id: string) {
    this.renderRoot.querySelector(`#${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  render() {
    return html`
      <div class="page-header">
        <div class="container">
          <div class="page-eyebrow">Documentation</div>
          <h1 class="page-title">Comprendre et utiliser Laundering</h1>
          <p class="page-sub">Fonctionnement de l'outil et lecture des résultats.</p>
        </div>
      </div>
      <div class="container layout">
        <aside class="toc">
          <div class="toc-label">Sur cette page</div>
          ${SECTIONS.map(([id, label]) => html`<button @click=${() => this.goTo(id)}>${label}</button>`)}
        </aside>
        <div>
          <section id="presentation">
            <h2>Présentation</h2>
            <p>Laundering interroge en une seule recherche plusieurs registres et bases publiques, puis centralise les résultats dans un rapport conservé. Il remplace la consultation site par site et uniformise la lecture grâce à un code couleur commun.</p>
            <div class="callout">${icons.info}<p>Laundering est une aide au criblage. Les voyants signalent des correspondances ; la conclusion « Conforme » ou « Non conforme » appartient toujours à l'enquêteur.</p></div>
          </section>

          <section id="sources">
            <h2>Les sources</h2>
            <table>
              <thead><tr><th>Code</th><th>Source</th><th>Mode d'interrogation</th></tr></thead>
              <tbody>
                <tr><td>GDA</td><td>Gel des avoirs (DG Trésor)</td><td>Copie locale du registre national, mise à jour automatiquement ; noms, prénoms et alias comparés.</td></tr>
                <tr><td>DL</td><td>Dilisense</td><td>Nombre de résultats uniquement : le voyant ne dépasse pas l'orange, ouvrir le lien pour le détail.</td></tr>
                <tr><td>OL</td><td>Offshore Leaks (ICIJ)</td><td>API de réconciliation. Figurer dans une fuite n'est pas une sanction.</td></tr>
                <tr><td>OS</td><td>OpenSanctions</td><td>API officielle si une clé est configurée, sinon voyant gris et lien de recherche manuelle.</td></tr>
              </tbody>
            </table>
          </section>

          <section id="voyants">
            <h2>Les voyants</h2>
            <p>Chaque source produit un voyant. La couleur est décidée par une règle unique, identique pour toutes les sources.</p>
            <table>
              <thead><tr><th>Voyant</th><th>Signification</th></tr></thead>
              <tbody>
                ${(Object.keys(STATES) as SignalState[]).map(
                  (s) => html`<tr>
                    <td><span class="state-cell"><ld-signal compact .code=${STATES[s].label} .state=${s}></ld-signal>${STATES[s].label}</span></td>
                    <td>${STATES[s].description}</td>
                  </tr>`,
                )}
              </tbody>
            </table>
            <ul>
              <li><strong>Rouge</strong> exige le nom et le prénom ; si une date de naissance est saisie, la source doit la confirmer.</li>
              <li>Une fiche dont la date de naissance contredit celle saisie est considérée comme un homonyme : elle reste visible dans le détail.</li>
              <li>L'état global est celui du voyant le plus préoccupant. Un voyant gris empêche de conclure au vert.</li>
            </ul>
          </section>

          <section id="interpretation">
            <h2>Interpréter un rapport</h2>
            <ul>
              <li><strong>Résumé</strong> : identité recherchée, voyants, nombre de sources sans correspondance.</li>
              <li><strong>Synthèse</strong> : sources à examiner et sources non vérifiées.</li>
              <li><strong>Conclusion de l'enquêteur</strong> : décision et commentaire. Conclure « Conforme » malgré un voyant non vert exige un commentaire.</li>
              <li><strong>Détail par voyant</strong> : fiches trouvées, similarité du nom, comparaison de la naissance, version des données et lien vers la source.</li>
            </ul>
          </section>

          <section id="historique">
            <h2>Historique et relance</h2>
            <p>Un rapport n'est jamais modifié après coup : c'est une trace de ce qui a été trouvé, à quelle date et avec quelle version des registres. « Relancer » crée un nouveau rapport lié au précédent. Si deux personnes modifient la même conclusion, la seconde est avertie au lieu d'écraser la première.</p>
            <p>Chaque rapport peut être téléchargé en PDF : le document est produit par le serveur à partir du criblage enregistré, avec les versions des données et la conclusion de l'enquêteur.</p>
          </section>

          <section id="acces">
            <h2>Accès et conservation</h2>
            <ul>
              <li><strong>Enquêteur</strong> : lance des recherches, consulte et conclut ses propres dossiers.</li>
              <li><strong>Superviseur</strong> : voit et conclut aussi les dossiers de toute l'équipe.</li>
              <li>Les comptes et les tokens d'accès sont gérés par l'administrateur du serveur.</li>
              <li>Les criblages sont supprimés automatiquement au-delà de la durée de conservation fixée (1 an, durée modifiable).</li>
            </ul>
            <div class="callout">${icons.info}<p>Quand une source limite le nombre de requêtes, Laundering la met en pause quelques minutes au lieu d'insister : ses voyants sont gris pendant ce temps et la page de recherche le signale.</p></div>
          </section>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-docs": PageDocs;
  }
}
